import type { LoginFormData, Radio } from "@src/types";

import { readPersisted, storage } from "@src/services/storage";

import { formatCnpj } from "@src/utils/formatCnpj";

/**
 * Registro próprio das sessões memorizadas.
 *
 * Não dá para guardá-las nas gavetas do `persist` do zustand
 * (`MeuPlayState-{idRadio}`): o `logout()` reseta o estado e o persist grava
 * esse estado vazio por cima da gaveta da rádio atual, apagando justamente a
 * sessão que queremos lembrar. Este registro é escrito só por
 * `rememberSession`/`forgetSession`, então sobrevive a logout e troca de sessão.
 */
const SESSIONS_KEY = "MemorizedSessions";

/** Prefixo das gavetas do `persist` (ver states/meuplay.ts) — só para migração. */
const STATE_KEY_PREFIX = "MeuPlayState-";

/** Troca de sessão pedida no player, a ser concluída pela tela de login. */
const PENDING_SWITCH_KEY = "pendingSessionSwitch";

/** Marca que o auto-login não deve mais acontecer nesta aba. */
const AUTO_LOGIN_SUPPRESSED_KEY = "autoLoginSuppressed";

export type MemorizedSession = {
  /**
   * Identidade da sessão: rádio + CNPJ. O nome da rádio sozinho NÃO serve —
   * a mesma rádio pode ser acessada por CNPJs diferentes, e essas sessões se
   * sobrescreveriam no registro.
   */
  key: string;
  idRadio: string;
  /**
   * Rótulo de exibição: sempre o NOME DA RÁDIO. Não usar `logged_in_as`, que
   * varia por rádio (ora nome do cliente, ora CNPJ) e não identifica qual rádio
   * está sendo escolhida.
   */
  label: string;
  /** Complemento que distingue sessões da mesma rádio: CNPJ ou nome do cliente. */
  detail: string;
  /** Epoch (ms) do último uso confirmado; `0` quando nunca registrado. */
  lastUsedAt: number;
  loginFormData: LoginFormData;
};

type SessionsMap = Record<string, MemorizedSession>;

function sessionKey(loginFormData: LoginFormData): string {
  return `${loginFormData.idRadio}|${loginFormData.cnpj || ""}`;
}

/** Texto de uma linha, para onde não cabem duas (ex.: `<option>`). */
export function sessionTitle(session: MemorizedSession): string {
  return session.detail
    ? `${session.label} | ${session.detail}`
    : session.label;
}

function buildSession(
  loginFormData: LoginFormData,
  radio: Radio | undefined,
  lastUsedAt: number,
): MemorizedSession {
  const idRadio = loginFormData.idRadio;
  return {
    key: sessionKey(loginFormData),
    idRadio,
    label: radio?.name || idRadio,
    // CNPJ identifica a unidade; sem ele, o nome do cliente é o que resta.
    detail: loginFormData.cnpj
      ? formatCnpj(loginFormData.cnpj)
      : radio?.logged_in_as || "",
    lastUsedAt,
    loginFormData,
  };
}

function readSessions(): SessionsMap {
  const raw = readPersisted(SESSIONS_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as SessionsMap;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeSessions(sessions: SessionsMap) {
  storage.setItem(SESSIONS_KEY, JSON.stringify(sessions));
}

/**
 * Sessões deixadas por versões anteriores, quando ainda eram lidas das gavetas
 * do zustand. Migradas uma única vez para o registro próprio — sem isso, quem
 * atualiza o app perderia as sessões já memorizadas.
 */
function migrateFromStateDrawers(): SessionsMap {
  const migrated: SessionsMap = {};
  for (const key of Object.keys(localStorage)) {
    if (!key.startsWith(STATE_KEY_PREFIX)) continue;
    const raw = readPersisted(key);
    if (!raw) continue;
    try {
      const { state } = JSON.parse(raw) as {
        state?: {
          loginFormData?: LoginFormData;
          radio?: Radio;
          lastUsedAt?: number;
        };
      };
      const { loginFormData, radio, lastUsedAt } = state ?? {};
      if (!loginFormData || !loginFormData.idRadio) continue;
      const candidate = buildSession(loginFormData, radio, lastUsedAt ?? 0);
      const known = migrated[candidate.key];
      if (known && known.lastUsedAt >= candidate.lastUsedAt) continue;
      migrated[candidate.key] = candidate;
    } catch {
      // gaveta corrompida: ignora
    }
  }
  return migrated;
}

/** Sessões memorizadas, da usada mais recentemente para a mais antiga. */
export function getMemorizedSessions(): MemorizedSession[] {
  let sessions = readSessions();
  if (Object.keys(sessions).length === 0) {
    sessions = migrateFromStateDrawers();
    if (Object.keys(sessions).length > 0) writeSessions(sessions);
  }
  return Object.values(sessions).sort(
    (a, b) => b.lastUsedAt - a.lastUsedAt || a.label.localeCompare(b.label),
  );
}

/** Memoriza (ou atualiza) a sessão após um login confirmado com a API. */
export function rememberSession(loginFormData: LoginFormData, radio?: Radio) {
  if (!loginFormData.idRadio) return;
  const session = buildSession(loginFormData, radio, Date.now());
  const sessions = readSessions();
  sessions[session.key] = session;
  writeSessions(sessions);
}

/**
 * Descarta as gavetas de estado das OUTRAS rádios ao trocar de namespace.
 *
 * Substitui um `storage.clear()`, que zerava o localStorage inteiro — levando
 * junto o registro de sessões memorizadas (e chaves de terceiros, como `uuid`).
 */
export function clearOtherRadioStates(currentKey: string) {
  for (const key of Object.keys(localStorage)) {
    if (key.startsWith(STATE_KEY_PREFIX) && key !== currentKey) {
      storage.removeItem(key);
    }
  }
}

/** Esquece uma sessão memorizada (`key` de `MemorizedSession`). */
export function forgetSession(key: string) {
  const sessions = readSessions();
  delete sessions[key];
  writeSessions(sessions);
}

/**
 * Desliga o auto-login pelo resto da aba. Usado quando o usuário demonstra que
 * quer decidir sozinho: clicou em "Sair", cancelou a contagem regressiva ou
 * mexeu no formulário. Também é o que impede o auto-login de repetir em loop
 * depois de uma tentativa.
 */
export function suppressAutoLogin() {
  try {
    sessionStorage.setItem(AUTO_LOGIN_SUPPRESSED_KEY, "1");
  } catch {
    // storage indisponível: segue sem a trava
  }
}

export function isAutoLoginSuppressed(): boolean {
  try {
    return sessionStorage.getItem(AUTO_LOGIN_SUPPRESSED_KEY) !== null;
  } catch {
    return true; // sem sessionStorage, não arrisca repetir
  }
}

/**
 * Registra a troca de sessão pedida no player. Fica no `sessionStorage` (e não
 * no `location.state`) porque entre o pedido e a tela de login há um `logout()`
 * assíncrono que dispara navegações próprias — que sobrescreveriam o state.
 */
export function setPendingSwitch(key: string) {
  try {
    sessionStorage.setItem(PENDING_SWITCH_KEY, key);
  } catch {
    // storage indisponível: a troca vira um logout comum
  }
}

/** Lê e consome a troca pendente (só vale para a próxima tela de login). */
export function takePendingSwitch(): string | null {
  try {
    const idRadio = sessionStorage.getItem(PENDING_SWITCH_KEY);
    if (idRadio !== null) sessionStorage.removeItem(PENDING_SWITCH_KEY);
    return idRadio;
  } catch {
    return null;
  }
}
